import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Subscription, TimeoutError} from 'rxjs';
import {QlBuilderQueryConverter} from '../../../../shared/ql/builder/ql-builder-query-converter';
import {QlBuilderViolation} from '../../../../shared/ql/builder/ql-builder-violation';
import {UiQlWidgetWorkbenchStateService} from '../../../service/domain/ui-ql-widget-workbench-state.service';
import {UiQlWidgetAssistService} from '../../../service/api/ui-ql-widget-assist.service';
import {UiQlWidgetAssistStatusDto} from '../../../model/dto/api/ui-ql-widget-assist-status.dto';
import {UiQlWidgetAssistRequestDto} from '../../../model/dto/api/ui-ql-widget-assist-request.dto';
import {UiQlWidgetAssistResponseDto} from '../../../model/dto/api/ui-ql-widget-assist-response.dto';
import {UiQlWidgetAssistProposalDto} from '../../../model/dto/api/ui-ql-widget-assist-proposal.dto';
import {UiQlWidgetAssistChangeDto} from '../../../model/dto/api/ui-ql-widget-assist-change.dto';
import {UiQlWidgetMapping, UiQlWidgetType} from '../../../model/dto/api/ui-ql-widget-mapping.dto';
import {UiQlAssistChatMessage} from '../../../interface/ui-ql-assist-chat-message.interface';
import {UiQlAssistChatError} from '../../../interface/ui-ql-assist-chat-error.interface';
import {UiQlAssistChatErrorKind} from '../../../interface/ui-ql-assist-chat-error-kind.interface';
import {UiQlWidgetWorkbenchMode} from '../../../interface/ui-ql-widget-workbench-mode.interface';
import {UiQlWidgetMappingDefaults} from '../../../logic/ui-ql-widget-mapping-defaults';
import {uiQlViolationText} from '../../../logic/ui-ql-violation-texts';
import {UiQlAssistStatusBadge} from '../../../interface/ui-ql-assist-status-badge.interface';
import {
    UI_QL_ASSIST_CHANGE_ICONS,
    UI_QL_ASSIST_NOT_CONFIGURED,
    UI_QL_ASSIST_PII_HINT,
    UI_QL_ASSIST_STATUS_BADGE_UNKNOWN,
    UI_QL_ASSIST_STATUS_BADGES,
    UI_QL_ASSIST_SUGGESTIONS,
    uiQlAssistDisabledReasonText,
} from '../../../logic/ui-ql-assist-texts';

/**
 * Tab "Chat" of the QL workbench - the LLM chat designer (requirement.md "Erweiterung:
 * LLM-Chat-Designer" L4, decision round 5 points 12-15).
 *
 * <h3>Flow of one turn</h3>
 * The user message goes to `POST /ui/qlWidgets/assist` together with the current builder state
 * (the builder stays leading: manual edits flow into the next turn), the last completed texts
 * (`history`, at most 8) and the page's `conversationId`. Per answer status:
 * - `applied`            - the proposal is loaded IMMEDIATELY into builder + preview (undo snapshot
 *                          first); the message shows the change list and "Rückgängig".
 * - `needsClarification` - the question is shown, the input stays active.
 * - `rejected`           - violations are shown, NOTHING is loaded; "Trotzdem in Builder laden" when
 *                          the converter can represent the draft (saving stays blocked by the policy) -
 *                          but never for a draft the probe could not execute (`probeFailed`, N4): loading
 *                          it would only run the failing query again in the preview.
 * - `unsupported`        - the explanation says why; nothing is loaded.
 * The chat stays on its tab after an apply; the preview on the right updates by itself.
 *
 * <h3>Safety</h3>
 * Everything the LLM writes (explanation, question, assumptions) and the change labels are
 * rendered via interpolation only - never `innerHTML` (capability matrix I-LLM4). The conversation
 * lives in {@link UiQlWidgetWorkbenchStateService}, so it survives switching the left tab; a turn
 * still running when the tab is left is cancelled (its answer discarded).
 *
 * Declared in `UiQlWidgetBuilderModule` (NgModule, fe_core_standards) - its only host is
 * `UiQlWidgetWorkbenchPage` of the same module.
 */
@Component({
    selector: 'ui-ql-assist-chat',
    templateUrl: './ui-ql-assist-chat.component.html',
    styleUrls: ['./ui-ql-assist-chat.component.scss'],
    standalone: false,
})
export class UiQlAssistChatComponent implements OnInit, OnDestroy {

    /** Maximum length of one message (requirement.md L3.3: 1..1000 characters). */
    static readonly MAX_MESSAGE_LENGTH = 1000;

    readonly maxMessageLength = UiQlAssistChatComponent.MAX_MESSAGE_LENGTH;
    readonly piiHint = UI_QL_ASSIST_PII_HINT;
    readonly notConfiguredText = UI_QL_ASSIST_NOT_CONFIGURED;
    readonly suggestions = UI_QL_ASSIST_SUGGESTIONS;
    readonly statusBadges = UI_QL_ASSIST_STATUS_BADGES;
    readonly changeIcons = UI_QL_ASSIST_CHANGE_ICONS;

    /** Status endpoint: `loading` (skeleton) -> `ready` (enabled or not) | `error`. */
    statusPhase: 'loading' | 'ready' | 'error' = 'loading';
    status: UiQlWidgetAssistStatusDto | null = null;
    statusErrorText: string | null = null;

    messages: UiQlAssistChatMessage[] = [];
    inputText: string = '';

    /** A turn is in flight: typing indicator, input locked, "Abbrechen" visible. */
    busy: boolean = false;

    /** "Start-Entität beibehalten" - sends `pinnedStartEntity`. */
    pinStartEntity: boolean = false;

    /** Start entity of the current builder model (`null` while the builder is empty). */
    startEntity: string | null = null;

    mode: UiQlWidgetWorkbenchMode = 'builder';
    canUndo: boolean = false;

    @ViewChild('messageList') messageList?: ElementRef<HTMLElement>;

    private turnSubscription: Subscription | null = null;
    private statusSubscription: Subscription | null = null;
    private readonly subscriptions = new Subscription();

    constructor(readonly state: UiQlWidgetWorkbenchStateService,
                private readonly assistService: UiQlWidgetAssistService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /** Subscribe to the page state and ask the status endpoint once. */
    initComponent(): void {

        this.subscriptions.add(this.state.chatMessages$.subscribe(messages => {
            this.messages = messages;
            this.scrollToEnd();
        }));
        this.subscriptions.add(this.state.canUndo$.subscribe(canUndo => this.canUndo = canUndo));
        this.subscriptions.add(this.state.mode$.subscribe(mode => this.mode = mode));
        this.subscriptions.add(this.state.model$.subscribe(model => {
            this.startEntity = model?.root?.entityName || null;
            if (!this.startEntity) {
                this.pinStartEntity = false;
            }
        }));

        this.loadStatus();
    }

    ngOnDestroy(): void {
        // leaving the tab mid-turn: the answer is discarded, the conversation shows it
        if (this.busy) {
            this.cancel();
        }
        this.statusSubscription?.unsubscribe();
        this.subscriptions.unsubscribe();
    }

    // ------------------------------------------------------------------
    // status
    // ------------------------------------------------------------------

    loadStatus(): void {

        this.statusSubscription?.unsubscribe();
        this.statusPhase = 'loading';
        this.statusErrorText = null;

        this.statusSubscription = this.assistService.getStatus().subscribe({
            next: status => {
                this.status = status;
                this.statusPhase = 'ready';
            },
            error: error => {
                this.status = null;
                this.statusPhase = 'error';
                this.statusErrorText = UiQlAssistChatComponent.isForbidden(error)
                    ? 'Keine Berechtigung: Für den KI-Assistenten ist das Schreibrecht auf UI-Widgets erforderlich.'
                    : 'Der Status des KI-Assistenten konnte nicht geladen werden.';
            },
        });
    }

    /**
     * Re-read the status WITHOUT the loading phase (QA #18): the per-user request counter
     * (`remainingUserRequests`) only changes server-side, so it is refreshed after every turn - answered,
     * failed or rate-limited - while the chat stays usable. A failed refresh keeps the last known status.
     */
    refreshStatusSilently(): void {

        if (this.statusPhase !== 'ready') {
            return;
        }

        this.statusSubscription?.unsubscribe();
        this.statusSubscription = this.assistService.getStatus().subscribe({
            next: status => {
                this.status = status;
            },
            error: () => { /* keep the last known counter */ },
        });
    }

    get enabled(): boolean {
        return this.statusPhase === 'ready' && this.status?.enabled === true;
    }

    get disabledReasonText(): string {
        return uiQlAssistDisabledReasonText(this.status?.reason);
    }

    // ------------------------------------------------------------------
    // input
    // ------------------------------------------------------------------

    get trimmedInput(): string {
        return (this.inputText ?? '').trim();
    }

    get canSend(): boolean {
        return this.enabled
            && !this.busy
            && this.trimmedInput.length > 0
            && this.trimmedInput.length <= UiQlAssistChatComponent.MAX_MESSAGE_LENGTH;
    }

    /** Enter sends, Shift+Enter inserts a line break (default behaviour of the textarea). */
    onInputKeydown(event: KeyboardEvent): void {

        if (event.key !== 'Enter' || event.shiftKey || event.isComposing) {
            return;
        }

        event.preventDefault();
        this.send();
    }

    /** A suggestion chip fills the input; the user can still edit before sending. */
    useSuggestion(text: string): void {
        if (this.busy) {
            return;
        }
        this.inputText = text;
    }

    send(): void {

        if (!this.canSend) {
            return;
        }

        const text = this.trimmedInput;
        this.inputText = '';
        this.runTurn(text, null);
    }

    /** "Wiederholen" on an error entry: resend the same text for the SAME user message. */
    retry(message: UiQlAssistChatMessage): void {

        if (this.busy || !this.enabled || !message.retryText) {
            return;
        }

        const userMessage = this.findUserMessageBefore(message.id);
        this.state.removeChatMessage(message.id);
        this.runTurn(message.retryText, userMessage?.id ?? null);
    }

    /** "Abbrechen": unsubscribe - the HTTP request is aborted and a late answer can never apply. */
    cancel(): void {

        if (!this.busy) {
            return;
        }

        const retryText = this.lastPendingUserText();
        this.turnSubscription?.unsubscribe();
        this.turnSubscription = null;
        this.busy = false;

        this.addError('cancelled', 'Anfrage abgebrochen – die Antwort wird verworfen.', true, retryText);
    }

    // ------------------------------------------------------------------
    // actions on an assistant answer
    // ------------------------------------------------------------------

    /** The ONE message whose "Rückgängig" is offered: the newest one that is still applied. */
    get undoTargetId(): number | null {
        for (let i = this.messages.length - 1; i >= 0; i--) {
            const m = this.messages[i];
            if (m.role === 'assistant' && m.applied && !m.undone) {
                return m.id;
            }
        }
        return null;
    }

    undo(message: UiQlAssistChatMessage): void {

        if (this.busy || message.id !== this.undoTargetId) {
            return;
        }

        if (this.state.undo()) {
            this.state.updateChatMessage(message.id, {applied: false, undone: true});
        }
    }

    /** "Trotzdem in Builder laden" for a `rejected` answer - the violations stay visible in the builder. */
    loadRejected(message: UiQlAssistChatMessage): void {

        const proposal = message.response?.proposal;

        if (this.busy || !proposal || !message.loadable || message.applied) {
            return;
        }

        const unmappable = this.applyProposal(proposal);

        if (unmappable.length) {
            this.state.updateChatMessage(message.id, {
                loadable: false,
                error: UiQlAssistChatComponent.error('unmappable', false, [], unmappable),
            });
            return;
        }

        this.state.updateChatMessage(message.id, {applied: true, undone: false});
    }

    violationText(violation: QlBuilderViolation): string {
        return uiQlViolationText(violation);
    }

    /** A rejected draft the probe could not execute (`probeFailed`): not offered for loading (N4). */
    static isNotExecutable(response: UiQlWidgetAssistResponseDto | null | undefined): boolean {
        return !!response?.validation?.violations?.some(v => v.code === 'probeFailed');
    }

    notExecutable(response: UiQlWidgetAssistResponseDto | null | undefined): boolean {
        return UiQlAssistChatComponent.isNotExecutable(response);
    }

    changeIcon(change: UiQlWidgetAssistChangeDto): string {
        return this.changeIcons[change.kind] ?? 'fa-solid fa-circle-dot text-color-secondary';
    }

    /** Badge of a turn status; an unknown status (newer backend) gets a neutral fallback badge, never an empty tag. */
    statusBadge(status: string): UiQlAssistStatusBadge {
        return Object.prototype.hasOwnProperty.call(this.statusBadges, status)
            ? this.statusBadges[status as keyof typeof UI_QL_ASSIST_STATUS_BADGES]
            : UI_QL_ASSIST_STATUS_BADGE_UNKNOWN;
    }

    // ------------------------------------------------------------------
    // turn
    // ------------------------------------------------------------------

    private runTurn(text: string, existingUserMessageId: number | null): void {

        const userMessageId = existingUserMessageId ?? this.state.addChatMessage(
            UiQlAssistChatComponent.message('user', text)).id;

        const request = this.buildRequest(text);

        this.busy = true;
        this.turnSubscription = this.assistService.assist(request).subscribe({
            next: response => this.onTurnResponse(response, userMessageId),
            error: error => this.onTurnError(error, text),
        });
    }

    private buildRequest(text: string): UiQlWidgetAssistRequestDto {

        // history = completed turns BEFORE this message (the message itself travels in `message`)
        const history = this.state.chatHistory();

        const request: UiQlWidgetAssistRequestDto = {
            conversationId: this.state.conversationId,
            message: text,
            history,
        };

        const qlQuery = this.state.buildQlQuery();
        if (qlQuery) {
            const s = this.state.snapshot;
            request.currentState = {qlQuery, widgetType: s.widgetType, mapping: s.mapping ?? {}};
        }

        if (this.pinStartEntity && this.startEntity) {
            request.pinnedStartEntity = this.startEntity;
        }

        return request;
    }

    private onTurnResponse(response: UiQlWidgetAssistResponseDto, userMessageId: number): void {

        this.busy = false;
        this.turnSubscription = null;

        this.state.updateChatMessage(userMessageId, {inHistory: true});

        const assistant = this.state.addChatMessage({
            ...UiQlAssistChatComponent.message('assistant', UiQlAssistChatComponent.historyText(response)),
            inHistory: true,
            response,
        });

        this.refreshStatusSilently();

        const proposal = response?.proposal;

        if (response?.status === 'applied') {

            if (!proposal) {
                this.state.updateChatMessage(assistant.id, {
                    error: UiQlAssistChatComponent.error('unmappable', false, [],
                        ['Die Antwort enthält keinen Entwurf.']),
                });
                return;
            }

            const unmappable = this.applyProposal(proposal);
            this.state.updateChatMessage(assistant.id, unmappable.length
                ? {error: UiQlAssistChatComponent.error('unmappable', false, [], unmappable)}
                : {applied: true});
            return;
        }

        if (response?.status === 'rejected' && proposal) {
            const converted = QlBuilderQueryConverter.fromQlQuery(proposal.qlQuery);
            this.state.updateChatMessage(assistant.id, {
                loadable: !!converted.model && !(converted.unmappable?.length)
                    && !UiQlAssistChatComponent.isNotExecutable(response),
            });
        }
    }

    private onTurnError(error: unknown, text: string): void {

        this.busy = false;
        this.turnSubscription = null;

        const classified = UiQlAssistChatComponent.classifyError(error);
        this.addError(classified.kind, classified.text, classified.retryable, classified.retryable ? text : null,
            classified.violations);

        if (classified.kind === 'notConfigured') {
            this.loadStatus();
        } else {
            this.refreshStatusSilently();
        }
    }

    /**
     * Load a draft into builder + preview: convert, push an undo snapshot, then model, widget type
     * and mapping. Deliberately NOT `applyDefinition` - that one replaces the saved-widget meta and
     * the "Zurücksetzen" baseline, which a chat draft must never do.
     *
     * @returns the converter's unmappable messages; non-empty = NOTHING was loaded
     */
    private applyProposal(proposal: UiQlWidgetAssistProposalDto): string[] {

        const converted = proposal?.qlQuery
            ? QlBuilderQueryConverter.fromQlQuery(proposal.qlQuery)
            : {model: null, unmappable: ['Der Entwurf enthält keine QL-Abfrage.']};

        if (converted.unmappable?.length || !converted.model) {
            return converted.unmappable?.length
                ? converted.unmappable
                : ['Der Entwurf kann im Builder nicht dargestellt werden.'];
        }

        const widgetType: UiQlWidgetType = proposal.widgetType ?? 'table';
        const mapping: UiQlWidgetMapping = UiQlAssistChatComponent.hasKeys(proposal.mapping)
            ? proposal.mapping
            : UiQlWidgetMappingDefaults.forType(widgetType, converted.model);

        this.state.pushUndoSnapshot();
        this.state.setModel(converted.model);
        this.state.setWidgetType(widgetType);
        this.state.setMapping(mapping);

        return [];
    }

    private addError(kind: UiQlAssistChatErrorKind,
                     text: string,
                     retryable: boolean,
                     retryText: string | null,
                     violations: QlBuilderViolation[] = []): void {

        this.state.addChatMessage({
            ...UiQlAssistChatComponent.message('error', text),
            error: UiQlAssistChatComponent.error(kind, retryable, violations, []),
            retryText,
        });
    }

    private findUserMessageBefore(id: number): UiQlAssistChatMessage | null {
        const index = this.messages.findIndex(m => m.id === id);
        for (let i = index - 1; i >= 0; i--) {
            if (this.messages[i].role === 'user') {
                return this.messages[i].inHistory ? null : this.messages[i];
            }
        }
        return null;
    }

    private lastPendingUserText(): string | null {
        const last = [...this.messages].reverse().find(m => m.role === 'user');
        return last && !last.inHistory ? last.text : null;
    }

    private scrollToEnd(): void {
        const element = this.messageList?.nativeElement;
        if (!element) {
            return;
        }
        setTimeout(() => element.scrollTop = element.scrollHeight);
    }

    // ------------------------------------------------------------------
    // static helpers
    // ------------------------------------------------------------------

    /** Text of an assistant answer as it goes back in `history`. */
    static historyText(response: UiQlWidgetAssistResponseDto): string {
        if (response?.status === 'needsClarification' && response.clarificationQuestion) {
            return response.clarificationQuestion;
        }
        return response?.explanation ?? '';
    }

    /**
     * German classification of a failed turn (the global toast is suppressed for this call, so this
     * is the ONLY place the user learns about it).
     */
    static classifyError(error: unknown): {
        kind: UiQlAssistChatErrorKind;
        text: string;
        retryable: boolean;
        violations: QlBuilderViolation[];
    } {

        if (error instanceof TimeoutError || (error as { name?: string })?.name === 'TimeoutError') {
            return {kind: 'timeout', retryable: true, violations: [],
                text: 'Zeitüberschreitung – der Assistent hat nicht rechtzeitig geantwortet.'};
        }

        const httpError = error as HttpErrorResponse;
        const status = httpError?.status;
        const body = httpError?.error;

        if (status === 429) {
            const seconds = UiQlAssistChatComponent.retryAfterSeconds(httpError);
            return {kind: 'rateLimited', retryable: true, violations: [],
                text: seconds != null
                    ? `Zu viele Anfragen – bitte kurz warten (ca. ${seconds} Sekunden).`
                    : 'Zu viele Anfragen – bitte kurz warten.'};
        }

        if (UiQlAssistChatComponent.isForbidden(error)) {
            return {kind: 'forbidden', retryable: false, violations: [],
                text: 'Keine Berechtigung: Für den KI-Assistenten ist das Schreibrecht auf UI-Widgets erforderlich.'};
        }

        if (status === 409) {
            return {kind: 'notConfigured', retryable: false, violations: [],
                text: `${UI_QL_ASSIST_NOT_CONFIGURED}.`};
        }

        if (status === 400 && Array.isArray(body?.violations) && body.violations.length) {
            return {kind: 'invalidState', retryable: false, violations: body.violations,
                text: 'Der aktuelle Stand im Builder verstößt gegen die Regeln – der Assistent wurde nicht aufgerufen. '
                    + 'Bitte zuerst die Verstöße beheben.'};
        }

        if (status === 408 || status === 504) {
            return {kind: 'timeout', retryable: true, violations: [],
                text: 'Zeitüberschreitung – der Assistent hat nicht rechtzeitig geantwortet.'};
        }

        if (status === 0) {
            return {kind: 'network', retryable: true, violations: [],
                text: 'Keine Verbindung zum Server. Bitte die Netzwerkverbindung prüfen.'};
        }

        return {kind: 'failed', retryable: true, violations: [],
            text: 'Die Anfrage an den Assistenten ist fehlgeschlagen.'};
    }

    /** Wait time of a 429: `Retry-After` header (seconds), else `retryAfterSeconds` in the body. */
    static retryAfterSeconds(error: HttpErrorResponse): number | null {

        const header = error?.headers?.get?.('Retry-After');
        const fromHeader = header != null ? Number(header) : NaN;
        if (Number.isFinite(fromHeader) && fromHeader > 0) {
            return Math.ceil(fromHeader);
        }

        const fromBody = Number(error?.error?.retryAfterSeconds);
        return Number.isFinite(fromBody) && fromBody > 0 ? Math.ceil(fromBody) : null;
    }

    static isForbidden(error: unknown): boolean {
        const status = (error as HttpErrorResponse)?.status;
        return status === 403 || status === 401;
    }

    private static hasKeys(mapping: UiQlWidgetMapping | null | undefined): mapping is UiQlWidgetMapping {
        return !!mapping && Object.keys(mapping).length > 0;
    }

    private static message(role: UiQlAssistChatMessage['role'], text: string): Omit<UiQlAssistChatMessage, 'id'> {
        return {
            role,
            text,
            inHistory: false,
            response: null,
            applied: false,
            undone: false,
            loadable: false,
            error: null,
            retryText: null,
        };
    }

    private static error(kind: UiQlAssistChatErrorKind,
                         retryable: boolean,
                         violations: QlBuilderViolation[],
                         unmappable: string[]): UiQlAssistChatError {
        return {kind, retryable, violations, unmappable};
    }
}
